'use client';

import { type ReactNode, useRef, useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@ps/ui/atoms/popover';
import { useScopedTheme } from '@ps/ui/theme/useScopedTheme';
import EmojiPicker, {
  type EmojiClickData,
  EmojiStyle,
  SkinTonePickerLocation,
  Theme,
} from 'emoji-picker-react';

import type { MessageReaction } from '../../types/MessageReaction';

interface Props {
  /**
   * The trigger that opens the picker. Renders inside a
   * `PopoverTrigger` so the consumer-supplied element keeps its
   * click handler / aria — typical use is a small icon button.
   */
  trigger: ReactNode;
  onSelect: (reaction: MessageReaction) => void;
}

/**
 * Emoji picker dropdown — wraps `emoji-picker-react` inside a shadcn
 * Popover. Follows the surrounding theme (see `pickerTheme`); native emoji
 * style so the rendered emoji match what the rest of the OS shows
 * in the bubble. Picker is unmounted while closed (only rendered
 * inside `PopoverContent`) so the heavy emoji data isn't loaded
 * until the user actually opens it.
 */
export function EmojiPickerPopover({ trigger, onSelect }: Props) {
  const [open, setOpen] = useState(false);
  // The picker draws its own chrome, so the app's roles cannot reach it — the
  // library takes the theme as a VALUE. Read it off the trigger's DOM scope;
  // the popover content is portalled and sits outside it.
  const triggerRef = useRef<HTMLSpanElement>(null);
  const scope = useScopedTheme(triggerRef, true);
  const pickerTheme = scope === 'light' ? Theme.LIGHT : Theme.DARK;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span ref={triggerRef} className="contents">
          {trigger}
        </span>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="bg-surface-1 w-auto border-none p-0 shadow-xl"
      >
        <EmojiPicker
          theme={pickerTheme}
          emojiStyle={EmojiStyle.NATIVE}
          skinTonePickerLocation={SkinTonePickerLocation.SEARCH}
          autoFocusSearch={false}
          lazyLoadEmojis
          onEmojiClick={(emoji: EmojiClickData) => {
            onSelect({
              emoji: emoji.emoji,
              emojiCode: emoji.unified,
            });
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
